<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <button class="close-btn" @click="closeModal">×</button>
      
      <h2 class="modal-title">兑换码</h2>
      
      <!-- 用户信息区域 -->
      <div class="user-info-section">
        <div class="info-row">
          <span class="info-label">用户名:</span>
          <span class="info-value">{{ userInfo.userToken }}</span>
        </div>
        <div v-if="userInfo.email" class="info-row">
          <span class="info-label">邮箱:</span>
          <span class="info-value">{{ userInfo.email }}</span>
        </div>
      </div>
      
      <!-- 兑换码输入区域 -->
      <div class="exchange-section">
        <label class="exchange-label">
          <span class="required">*</span> 兑换码
        </label>
        <input 
          type="text" 
          v-model="exchangeCode"
          class="exchange-input"
          placeholder="请输入兑换码（最少20位）"
          @keyup.enter="handleExchange"
          :disabled="isLoading"
        />
        <span data-v-189e887a="" style="color: rgb(48, 51, 232); text-align: justify;">您兑换的套餐会立即生效，没有使用完的套餐会在此兑换套餐到期后继续使用。详情可在个人中心查看</span>
        <!-- 错误提示 -->
        <div v-if="errorMessage" class="error-message">
          <span class="error-icon">⚠️</span>
          {{ errorMessage }}
        </div>
        
        <!-- 成功提示 -->
        <div v-if="successMessage" class="success-message">
          <span class="success-icon">✅</span>
          {{ successMessage }}
        </div>
      </div>
      
      <!-- 按钮区域 -->
      <div class="button-section">
        <button class="cancel-btn" @click="closeModal" :disabled="isLoading">取消</button>
        <button 
          class="confirm-btn" 
          @click="handleExchange"
          :disabled="!exchangeCode.trim() || isLoading"
        >
          <span v-if="!isLoading">立即兑换</span>
          <span v-else class="loading-text">
            <span class="loading-spinner"></span>
            兑换中...
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { exchange, getUserInfo } from '../api/home'
import { useRouter } from 'vue-router'

const router = useRouter()

defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: () => ({
      userToken: '',
      email: ''
    })
  }
})

const emit = defineEmits(['close', 'exchange'])

const exchangeCode = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const closeModal = () => {
  exchangeCode.value = ''
  errorMessage.value = ''
  successMessage.value = ''
  isLoading.value = false
  emit('close')
}

const validateExchangeCode = (code) => {
  if (!code || code.length < 20) {
    return '兑换码错误'
  }
  return null
}

const handleExchange = async () => {
  const code = exchangeCode.value.trim()
  
  errorMessage.value = ''
  successMessage.value = ''
  
  const validationError = validateExchangeCode(code)
  if (validationError) {
    errorMessage.value = validationError
    return
  }
  
  isLoading.value = true
  
  try {
    const response = await exchange(code)
    
    if (response.code === 1) {
      successMessage.value = response.msg || '兑换成功！'
      
      try {
        const userResponse = await getUserInfo()
        if (userResponse.code === 1) {
          emit('exchange', code, userResponse.data)
        } else {
          emit('exchange', code)
        }
      } catch (userError) {
        console.error('获取用户信息失败:', userError)
        emit('exchange', code)
      }
      
      // 兑换成功后延迟关闭弹窗，给用户时间看到成功消息
      setTimeout(() => {
        closeModal()
      }, 300)
    } else if (response.code === -1) {
      closeModal()
      router.push('/login')
    } else {
      errorMessage.value = response.msg || '兑换失败，请重试'
    }
  } catch (error) {
    console.error('兑换请求错误:', error)
    errorMessage.value = '网络错误，请检查连接后重试'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  min-width: 320px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  animation: slideUp 0.3s ease;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
  transform: scale(1.1);
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin: 0 0 20px 0;
  text-align: center;
}

.user-info-section {
  background: #f5f5f7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 0;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.exchange-section {
  margin-bottom: 24px;
}

.exchange-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.exchange-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.exchange-input::placeholder {
  color: #999;
}

.exchange-input:focus {
  border-color: #7b5cff;
  box-shadow: 0 0 0 2px rgba(123, 92, 255, 0.1);
}

.exchange-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}

.error-message {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #ff4d4f;
  font-size: 13px;
}

.error-icon {
  margin-right: 6px;
  font-size: 14px;
}

.success-message {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  color: #52c41a;
  font-size: 13px;
}

.success-icon {
  margin-right: 6px;
  font-size: 14px;
}

.button-section {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn,
.confirm-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 80px;
}

.cancel-btn {
  background: #fff;
  color: #666;
  border: 1px solid #d9d9d9;
}

.cancel-btn:hover {
  color: #333;
  border-color: #333;
}

.confirm-btn {
  background: linear-gradient(135deg, #7b5cff, #9b7bff);
  color: white;
  box-shadow: 0 4px 12px rgba(123, 92, 255, 0.3);
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(123, 92, 255, 0.4);
}

.confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .modal-container {
    width: 90%;
    max-width: 400px;
    padding: 20px;
  }
}
</style>

